import { useEffect, useState } from "react";

import type { FetchApi } from "@common/hooks/useFetchApi";
import type { AiNotice, AiNoticeAcknowledgement } from "@common/schemas/ai-notice";
import {
  fetchAiNoticeAcknowledgement,
  isAcknowledgementDue,
  parseAiNotice,
  saveAiNoticeAcknowledgement,
} from "@common/utils/ai-notice-gate";

interface UseAiNoticeGateInput {
  orgId: string | null;
  userId: string | null;
  orgMetadata: unknown;
  fetchWeb: FetchApi;
}

interface UseAiNoticeGateResult {
  pendingNotice: AiNotice | null;
  acknowledge: () => void;
}

function ignoreRejectedSave(): void {}

export function useAiNoticeGate({
  orgId,
  userId,
  orgMetadata,
  fetchWeb,
}: UseAiNoticeGateInput): UseAiNoticeGateResult {
  const notice = parseAiNotice(orgMetadata);
  const [pendingNotice, setPendingNotice] = useState<AiNotice | null>(null);

  useEffect(() => {
    if (!orgId || !userId || !notice) {
      setPendingNotice(null);
      return;
    }
    const activeNotice = notice;
    let cancelled = false;

    function showNoticeIfDue(acknowledgement: AiNoticeAcknowledgement | null): void {
      if (cancelled) return;
      setPendingNotice(isAcknowledgementDue(activeNotice, acknowledgement, new Date()) ? activeNotice : null);
    }

    function hideNoticeOnLookupFailure(): void {
      if (cancelled) return;
      setPendingNotice(null);
    }

    fetchAiNoticeAcknowledgement(fetchWeb).then(showNoticeIfDue, hideNoticeOnLookupFailure);

    return () => {
      cancelled = true;
    };
  }, [orgId, userId, notice?.version, notice?.reprompt_days, fetchWeb]);

  function acknowledge(): void {
    if (!pendingNotice) return;
    setPendingNotice(null);
    saveAiNoticeAcknowledgement(fetchWeb, pendingNotice.version).catch(ignoreRejectedSave);
  }

  return { pendingNotice, acknowledge };
}
